import "./view.scss";
import axios from "axios";

class SearchResults {
  constructor(block) {
    this.block = block;
    this.searchContainer = document.querySelector(".wp-block-thirtysix-blocks-search-results"); // Use the block itself as the search container
    this.searchInput = block.querySelector(".search-input");
    this.searchClear = block.querySelector(".search-clear");
    this.dropdown = block.querySelector(".search-results-dropdown");
    this.resultsGrid = block.querySelector(".search-results-grid");
    this.searchTermDisplay = block.querySelector(".search-term-display");
    this.searchIconOpenButtons = document.querySelectorAll(".search-container-open .wp-element-button");
    this.searchIconClose = block.querySelector(".search-container-close");
    
    this.postType = block.dataset.postType || "products";
    this.postsPerPage = parseInt(block.dataset.postsPerPage) || 8;
    this.searchTimeout = null;
    this.isLoading = false;

    this.init();
  }

  init() {
    if (!this.searchInput) return;

    // Input event with debounce
    this.searchInput.addEventListener("input", (e) => {
      this.handleSearch(e.target.value);
    });

    // Clear button
    this.searchClear.addEventListener("click", () => {
      this.clearSearch();
    });

    // Close dropdown when clicking outside
    document.addEventListener("click", (e) => {
      if (!this.block.contains(e.target)) {
        this.hideDropdown();
      }
    });

    // Prevent dropdown from closing when clicking inside
    this.dropdown.addEventListener("click", (e) => {
      e.stopPropagation();
    });

    // Add event listeners to all open buttons
    if (this.searchIconOpenButtons && this.searchIconOpenButtons.length > 0) {
      this.searchIconOpenButtons.forEach((button) => {
        button.addEventListener("click", (e) => {
          e.preventDefault();
          this.searchContainer.classList.add('show-result');
          this.searchClear.click();
          this.searchInput.focus();
        });
      });
    }
    // shared close handler
    const closeSearch = () => {
      this.searchContainer.classList.remove('show-result');
      this.searchClear.click();
    };

    // click close icon
    this.searchIconClose.addEventListener("click", closeSearch);

    // Esc key = same behavior
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        closeSearch();
      }
    });

    // Handle escape key
    this.searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.clearSearch();
        this.searchInput.blur();
      }
    });
  }

  handleSearch(searchTerm) {
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    const trimmedTerm = searchTerm.trim();

    // Show/hide clear button
    if (trimmedTerm.length > 0) {
      this.searchClear.style.display = "block";
    } else {
      this.searchClear.style.display = "none";
      this.hideDropdown();
      return;
    }

    // Minimum 2 characters before searching
    if (trimmedTerm.length < 2) {
      this.hideDropdown();
      return;
    }

    // Debounce search (reduced to 250ms for better responsiveness)
    this.searchTimeout = setTimeout(() => {
      this.performSearch(trimmedTerm);
    }, 250);
  }

  async performSearch(searchTerm) {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoadingState();
    

    try {
      // Use query parameters instead of path parameters to handle special characters
      const endpoint = `/wp-json/thirtysix-blocks/v1/search`;
      const params = {
        search_term: searchTerm,
        post_type: this.postType,
        posts_per_page: this.postsPerPage
      };
      
      const response = await axios.get(endpoint, { params });

      if (response && response.data) {
        // Check if we actually got posts (filter out empty objects)
        const validPosts = response.data.posts.filter(post => post && post.id);
        if (validPosts.length > 0) {
          this.dropdown.classList.remove('no-results');
          this.searchContainer.classList.remove('search-loading');
          this.renderResults(validPosts, searchTerm);
        } else {
          this.dropdown.classList.add('no-results');
          this.searchContainer.classList.remove('search-loading');
          this.renderNoResults(searchTerm);
        }
      } else {
        this.dropdown.classList.add('no-results');
        this.searchContainer.classList.remove('search-loading');
        this.renderNoResults(searchTerm);
      }
    } catch (error) {
      console.error("Search error:", error);
      this.renderError();
    } finally {
      this.isLoading = false;
      this.searchContainer.classList.remove('search-loading');
    }
  }

  showLoadingState() {
    this.searchContainer.classList.add('search-loading');
    this.resultsGrid.innerHTML = `
      <div class="search-loading">
        <p>Search in progress...</p>
        <div class="spinner"></div>
      </div>
    `;
    this.showDropdown();
  }

  renderResults(posts, searchTerm) {
    if (!posts || posts.length === 0) {
      this.renderNoResults(searchTerm);
      return;
    }

    this.searchTermDisplay.textContent = searchTerm;

    let html = "";
    posts.forEach((post) => {
      const thumbnail = post.thumbnail || "";
      const categories = post.categories || [];
      const tags = post.tags || [];

      let tagsHtml = "";
      if (tags.length > 0) {
        tagsHtml = '<div class="product-tags">';
        tags.forEach((tag) => {
          tagsHtml += `<span class="tag" style="background-color: ${tag.accent_color}">${this.escapeHtml(tag.name)}</span>`;
        });
        tagsHtml += "</div>";
      }

      let categoriesHtml = "";
      if (categories.length > 0) {
        categoriesHtml = '<div class="product-categories">';
        categories.forEach((category) => {
          categoriesHtml += `<span class="category">${this.escapeHtml(category)}</span>`;
        });
        categoriesHtml += "</div>";
      }

      html += `
        <a href="${this.escapeHtml(post.permalink)}" class="search-result-item">
          <div class="product-image">
            ${thumbnail ? `<img src="${this.escapeHtml(thumbnail)}" alt="${this.escapeHtml(post.title)}" loading="lazy" />` : ""}
            ${tagsHtml}
          </div>
          <div class="product-info">
            ${categoriesHtml}
            <div class="product-title">${this.escapeHtml(post.title)}</div>
          </div>
        </a>
      `;
    });

    this.resultsGrid.innerHTML = html;
    this.showDropdown();
  }

  renderNoResults(searchTerm) {
    this.searchTermDisplay.textContent = searchTerm;
    this.resultsGrid.innerHTML = `
      <div class="search-no-results">
        <p>No results found for “${this.escapeHtml(searchTerm)}”. <br/>Give another word a try!</p>
      </div>
    `;
    this.showDropdown();
  }

  renderError() {
    this.resultsGrid.innerHTML = `
      <div class="search-error">
        <p>An error occurred while searching. Please try again.</p>
      </div>
    `;
    this.showDropdown();
  }

  showDropdown() {
    this.dropdown.style.display = "block";
  }

  hideDropdown() {
    this.dropdown.style.display = "none";
  }

  clearSearch() {
    this.searchInput.value = "";
    this.searchClear.style.display = "none";
    this.hideDropdown();
    this.resultsGrid.innerHTML = "";
    this.searchTermDisplay.textContent = "";

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize all search result blocks
document.addEventListener("DOMContentLoaded", () => {
  const blocks = document.querySelectorAll(
    ".wp-block-thirtysix-blocks-search-results"
  );
  blocks.forEach((block) => {
    new SearchResults(block);
  });
});